import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  ColumnFiltersState,
  FilterFn
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Divider,
  Box
} from '@mui/material';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import Pagination from '../shared/pagination';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { createBonusColumns } from './columns';
import { BonusOverviewToolBar } from './toolbars/bonus-toolbar';
import { SortableTableHeader } from '../shared/sortable-header';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { Loading } from '../../atomic';
import { Bonus } from '@pxtra-client-lib-legacy/gen';
import i18n from 'i18next';
import { format } from 'date-fns';
import Util from '../../../../misc/util';

interface BonusOverviewTableProps {
  bonusData: Bonus[];
  isLoading: boolean;
}

export const BonusOverviewTable = observer(({ bonusData, isLoading }: BonusOverviewTableProps) => {
  const { t } = useTranslation();

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterFn: FilterFn<any> = (row, _columnId, filterValue) => {
    const searchTerm = filterValue.toLowerCase();

    return Object.entries(row.original).some(([key, value]) => {
      if (key === 'upcoming_payout_month' && value) {
        try {
          const formattedMonth = format(new Date(value as string), 'MMMM yy', {
            locale: Util.getLocale()
          });
          return formattedMonth.toLowerCase().includes(searchTerm);
        } catch {
          return false;
        }
      }

      const stringValue = String(value).toLowerCase();

      if (stringValue.includes(searchTerm)) {
        return true;
      }

      if (key === 'status' && typeof value === 'string') {
        const statusOptions = ['ACTIVE', 'PAUSED', 'WAITING', 'COMPLETED', 'CANCELED'];

        return statusOptions.some((status) => {
          if (value === status) {
            const translationBonus = i18n.t(`bonus:${status}`).toLowerCase();
            return translationBonus.includes(searchTerm);
          }
          return false;
        });
      }

      if (key === 'tax_treatment' && typeof value === 'string') {
        const taxOptions = ['SPECIAL_OCCASION', 'NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED'];

        return taxOptions.some((taxTreatment) => {
          if (value === taxTreatment) {
            const translation = i18n.t(`bonus:${taxTreatment}:shortName`).toLowerCase();
            return translation.includes(searchTerm);
          }
          return false;
        });
      }

      if (key === 'frequency' && typeof value === 'string') {
        const temporalOptions = ['RECURRING', 'ONE_TIME'];

        return temporalOptions.some((temporalOption) => {
          if (value === temporalOption) {
            const translation = i18n.t(`bonus:${temporalOption}`).toLowerCase();
            return translation.includes(searchTerm);
          }
          return false;
        });
      }

      return false;
    });
  };

  const columns = createBonusColumns(t);

  const table = useReactTable({
    data: bonusData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterFn,
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    sortDescFirst: false,
    state: {
      pagination,
      globalFilter,
      columnFilters
    }
  });

  if (isLoading) {
    return (
      <Paper className="p-8">
        <Box className="flex justify-center items-center">
          <Loading />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className="overflow-hidden flex flex-col">
      <BonusOverviewToolBar table={table} />
      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 400px)')}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader key={header.id} header={header} />
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center py-8 gap-y-2">
                    <img src={EmptyTable} className="w-64 h-32" alt="Empty Table" />
                    <div className="font-medium text-base">
                      {t('common:EMPTY_TABLE_TITLE_rows')}
                    </div>
                    <div className="text-grey-600 text-sm">{t('bonus:emptyTableHelperText')}</div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="py-2 border-grey-100" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider className="border-grey-100" />
      <Pagination table={table} />
    </Paper>
  );
});
